import {ControlledInput} from '@hm/fundamental'
import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormTrigger,
  useWatch
} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

import {type AdvancedFiltersFormValues, compounds} from './RefractoryAdvancedFilters.types'
import {RefractoryFilterResetButton} from './RefractoryFilterResetButton'

interface RefractoryChemicalDataTabProps {
  control: Control<AdvancedFiltersFormValues>
  hidden: boolean
  setValue: UseFormSetValue<AdvancedFiltersFormValues>
  trigger: UseFormTrigger<AdvancedFiltersFormValues>
}

export const RefractoryChemicalDataTab: React.FC<RefractoryChemicalDataTabProps> = ({
  control,
  hidden,
  setValue,
  trigger
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [chemicalData, csRatio] = useWatch({control, name: ['chemicalData', 'csRatio']})

  return (
    <div
      role="tabpanel"
      aria-label={t('refraCatalog.filters.chemicalData')}
      hidden={hidden}
      data-test-id="refra_catalog_chemical_filters"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_64px_64px_32px] items-start gap-3 sm:grid-cols-[minmax(0,1fr)_91px_91px_48px]">
        <span />
        <span className="text-center text-14px leading-[18px] text-foreground_faded">
          {t('refraCatalog.filters.min')}
        </span>
        <span className="text-center text-14px leading-[18px] text-foreground_faded">
          {t('refraCatalog.filters.max')}
        </span>
        <span />
        {[...compounds, 'csRatio'].map((compound, index) => {
          const label =
            compound === 'csRatio'
              ? t('refraCatalog.filters.csRatio')
              : compound.replace(/Plus/g, '+')

          return (
            <div key={compound} className="contents">
              <span className="flex min-h-12 min-w-0 items-center break-words text-16px">
                {label}
              </span>
              {(['min', 'max'] as const).map((bound) => (
                <Controller
                  key={bound}
                  name={
                    compound === 'csRatio' ? `csRatio.${bound}` : `chemicalData.${index}.${bound}`
                  }
                  control={control}
                  render={({field, fieldState}) => (
                    <ControlledInput
                      {...field}
                      onChange={(event) => {
                        field.onChange(event)
                        void trigger(compound === 'csRatio' ? 'csRatio' : `chemicalData.${index}`)
                      }}
                      type="number"
                      min={0}
                      max={100}
                      step="any"
                      placeholder="-"
                      aria-label={`${label} ${t(`refraCatalog.filters.${bound}`)}`}
                      aria-invalid={Boolean(fieldState.error)}
                      error={fieldState.error?.message}
                      isTouched
                      className="min-w-0"
                      data-cy={`refra-catalog-${compound}-${bound}`}
                    />
                  )}
                />
              ))}
              <RefractoryFilterResetButton
                name={compound}
                label={label}
                range={compound === 'csRatio' ? csRatio : chemicalData[index]}
                onReset={() =>
                  setValue(
                    compound === 'csRatio' ? 'csRatio' : `chemicalData.${index}`,
                    {min: '', max: ''},
                    {shouldDirty: true, shouldValidate: true}
                  )
                }
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
